import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../services/apiConfig';

interface SubscriptionStatus {
  has_active_subscription: boolean;
  status: string | null;
  is_cancelled?: boolean;
  plan_id?: number | null;
  plan_name?: string | null;
  billing_cycle?: string | null;
  price?: number | null;
  days_remaining: number | null;
  next_billing_date: string | null;
  expires_at?: string | null;
  assigned_at?: string | null;
  message: string;
}

interface SubscriptionContextType {
  subscription: SubscriptionStatus | null;
  loading: boolean;
  error: string | null;
  checkSubscription: () => Promise<void>;
  startTrial: (paypalSubscriptionId: string) => Promise<boolean>;
  cancelSubscription: () => Promise<boolean>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

interface SubscriptionProviderProps {
  children: ReactNode;
}

export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({ children }) => {
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const getToken = () => {
    return localStorage.getItem('token');
  };

  const checkSubscription = async () => {
    const token = getToken();
    console.log('🔍 checkSubscription called - token exists:', !!token);
    
    if (!token) {
      console.log('❌ No token found - setting subscription to null');
      setSubscription(null);
      return;
    }

    console.log('📡 Starting subscription check...');
    setLoading(true);
    setError(null);

    try {
      console.log('🌐 Making request to:', `${API_BASE_URL}/api/subscription/status`);
      const response = await axios.get<SubscriptionStatus>(
        `${API_BASE_URL}/api/subscription/status`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log('✅ Subscription status received:', response.data);
      setSubscription(response.data);
    } catch (err: any) {
      console.error('❌ Error checking subscription:', err);
      console.error('Error status:', err.response?.status);
      console.error('Error data:', err.response?.data);
      
      // If token is invalid/expired (401), clear it and redirect to login
      if (err.response?.status === 401) {
        console.warn('🔒 Token expired or invalid - clearing localStorage');
        localStorage.removeItem('token');
        setSubscription(null);
        setError('Session expired. Please log in again.');
      } else {
        setError(err.response?.data?.detail || 'Failed to check subscription status');
        setSubscription(null);
      }
    } finally {
      console.log('✅ checkSubscription completed - setting loading to false');
      setLoading(false);
    }
  };

  const startTrial = async (paypalSubscriptionId: string): Promise<boolean> => {
    const token = getToken();
    if (!token) {
      setError('Not authenticated');
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/subscription/start-trial`,
        {
          plan: 'face_capture_monthly',
          paypal_subscription_id: paypalSubscriptionId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.status === 200) {
        // Refresh subscription status
        await checkSubscription();
        return true;
      }
      return false;
    } catch (err: any) {
      console.error('Error starting trial:', err);
      
      // If token is invalid/expired (401), clear it
      if (err.response?.status === 401) {
        console.warn('Token expired or invalid during start trial');
        localStorage.removeItem('token');
        setError('Session expired. Please log in again.');
      } else {
        setError(err.response?.data?.detail?.message || 'Failed to start trial');
      }
      return false;
    } finally {
      setLoading(false);
    }
  };

  const cancelSubscription = async (): Promise<boolean> => {
    const token = getToken();
    if (!token) {
      setError('Not authenticated');
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/subscription/cancel`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        // Refresh subscription status
        await checkSubscription();
        return true;
      }
      return false;
    } catch (err: any) {
      console.error('Error cancelling subscription:', err);
      setError(err.response?.data?.detail || 'Failed to cancel subscription');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Check subscription on mount and when auth changes
  useEffect(() => {
    const token = getToken();
    console.log('🔐 SubscriptionContext mounted - token exists:', !!token);
    
    if (token) {
      console.log('✅ Token found on mount, calling checkSubscription...');
      checkSubscription();
    } else {
      console.log('❌ No token found on mount');
      setSubscription(null);
    }
    
    // Listen for custom event triggered after login
    const handleLoginSuccess = () => {
      console.log('🎉 Login success event received - checking subscription...');
      checkSubscription();
    };

    // Listen for custom event triggered after logout
    const handleLogout = () => {
      console.log('👋 Logout event received - clearing subscription...');
      setSubscription(null);
      setError(null);
    };
    
    window.addEventListener('loginSuccess', handleLoginSuccess);
    window.addEventListener('logout', handleLogout);
    
    return () => {
      window.removeEventListener('loginSuccess', handleLoginSuccess);
      window.removeEventListener('logout', handleLogout);
    };
  }, []);

  const value: SubscriptionContextType = useMemo(() => ({
    subscription,
    loading,
    error,
    checkSubscription,
    startTrial,
    cancelSubscription,
  }), [subscription, loading, error, checkSubscription, startTrial, cancelSubscription]);

  return <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>;
};
